// src/utils/backfillPhotoVariants.js — v0.102.0 (2026-07-22)
// ---------------------------------------------------------------------------
// Fill in the downscaled copies for photos captured BEFORE v0.101.0.
//
// WHY IN THE BROWSER
//   The obvious answer is a server-side batch with sharp. DropPin's functions
//   run inside a Static Web App, where native image binaries are awkward at
//   best, and a local script would need the SQL and storage connection strings
//   handed around. The browser already has canvas and an authenticated session,
//   and it reuses the exact downscaler that new captures use — so a backfilled
//   photo is byte-for-byte the same treatment as a fresh one.
//
// WHY IT MATTERS
//   Accepting a report copies its photos to the recipient. Doing that while
//   every photo is a 3-6 MB camera original is what made the enquiry payload
//   30 MB and the PDF unemailable. Backfill first, then accept.
//
// SAFETY
//   • The ORIGINAL is never touched — this only writes ThumbnailUrl/DisplayUrl.
//   • A photo that already has both is skipped, so it is safe to re-run.
//   • One failure never stops the batch; it is counted and reported.
//   • Sequential on purpose: a phone rebuilding 20 full-size images in parallel
//     will run itself out of memory.

import { makeImageVariants } from './imageVariants';

// Fetch an image URL and return it as a data URL, so canvas can read it without
// tainting (the signed blob URLs are same-origin-safe but CORS-fetched here).
async function urlToDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('fetch failed: ' + res.status);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(blob);
  });
}

// photos: rows as returned by /api/defect-photos (id, url, thumbnailUrl, displayUrl).
// onProgress({ done, total, skipped, failed }) is called after each photo.
// Returns { total, updated, skipped, failed }.
export async function backfillPhotoVariants(photos, apiPatch, onProgress) {
  const list = Array.isArray(photos) ? photos : [];
  let updated = 0, skipped = 0, failed = 0, done = 0;

  for (const p of list) {
    try {
      if (!p || !p.id || !p.url) { skipped++; continue; }
      // Already done — nothing to rebuild.
      if (p.thumbnailUrl && p.displayUrl) { skipped++; continue; }
      // eslint-disable-next-line no-await-in-loop
      const dataUrl = await urlToDataUrl(p.url);
      // eslint-disable-next-line no-await-in-loop
      const variants = await makeImageVariants(dataUrl);
      const patch = {};
      if (!p.thumbnailUrl && variants.thumbnailUrl) patch.thumbnailUrl = variants.thumbnailUrl;
      if (!p.displayUrl   && variants.displayUrl)   patch.displayUrl   = variants.displayUrl;
      if (!Object.keys(patch).length) {
        // The original is already smaller than both targets — legitimately
        // nothing to store, and re-running will keep skipping it.
        skipped++;
      } else {
        // eslint-disable-next-line no-await-in-loop
        await apiPatch(p.id, patch);
        updated++;
      }
    } catch (e) {
      failed++;
      // v0.104.0 — a silent counter told us 21 failed and nothing about why.
      // One line per failure in the console is what turns "it failed" into a
      // diagnosis without another deploy cycle.
      // eslint-disable-next-line no-console
      console.error('[optimise] photo ' + (p && p.id) + ' failed:', (e && e.message) ? e.message : e);
    } finally {
      done++;
      if (typeof onProgress === 'function') onProgress({ done, total: list.length, updated, skipped, failed });
    }
  }
  return { total: list.length, updated, skipped, failed };
}
